import { useState } from 'react';

import type { ResolvedChannel } from '../../api/types';
import { useConnectVkGroup, useRegisterVkPersonal, useResolveChannel } from '../../hooks/use-channels';
import { Avatar } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../ui/toast';

export interface ConnectChannelModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * U5.5 — connect by link/nickname/id: the server resolves the query first and
 * the user confirms a card with the real name and avatar before anything is
 * saved. A community additionally needs its access token; a personal account
 * needs none (I2 — the server never holds personal credentials).
 */
export function ConnectChannelModal({ open, onClose }: ConnectChannelModalProps) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<ResolvedChannel | null>(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const resolve = useResolveChannel();
  const connectGroup = useConnectVkGroup();
  const registerPersonal = useRegisterVkPersonal();
  const toast = useToast();

  if (!open) return null;

  const pending = connectGroup.isPending || registerPersonal.isPending;

  function reset() {
    setQuery('');
    setFound(null);
    setToken('');
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function search(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      setFound(await resolve.mutateAsync(query.trim()));
    } catch (err) {
      setFound(null);
      setError(err instanceof Error ? err.message : 'Не удалось найти такой аккаунт или сообщество');
    }
  }

  async function connect() {
    if (!found) return;
    setError(null);
    try {
      if (found.type === 'group') {
        await connectGroup.mutateAsync({ title: found.name, vk_group_id: found.vk_id, token });
      } else {
        await registerPersonal.mutateAsync({
          vk_user_id: found.vk_id,
          name: found.name,
          photo_url: found.photo_url,
          screen_name: found.screen_name,
        });
      }
      toast.push('Канал подключён', 'success');
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось подключить канал');
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-label="Подключить канал" className="w-full max-w-md rounded bg-card p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Подключить канал</h2>
          <button type="button" onClick={close} aria-label="Закрыть" className="text-muted-fg hover:text-fg">
            ✕
          </button>
        </div>

        <form onSubmit={(e) => void search(e)}>
          <label className="mb-1 block text-xs font-medium text-muted-fg" htmlFor="channel-query">
            Ссылка, никнейм или ID
          </label>
          <div className="flex gap-2">
            <Input
              id="channel-query"
              className="flex-1"
              placeholder="https://vk.com/club1, @durov или 12345"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              required
            />
            <Button type="submit" variant="secondary" disabled={resolve.isPending || query.trim() === ''}>
              {resolve.isPending ? 'Ищу…' : 'Найти'}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-muted-fg">
            Подойдёт ссылка на сообщество или профиль, короткое имя со знаком @ или числовой ID.
          </p>
        </form>

        {found && (
          <div className="mt-4 rounded border border-border p-3" data-testid="resolved-channel">
            <div className="flex items-center gap-3">
              <Avatar name={found.name} photoUrl={found.photo_url} size="md" />
              <div className="min-w-0">
                <div className="truncate font-medium">{found.name}</div>
                <div className="truncate text-xs text-muted-fg">
                  {found.type === 'group' ? 'Сообщество' : 'Личный аккаунт'} ·{' '}
                  {found.screen_name ? `vk.com/${found.screen_name}` : `id ${found.vk_id}`}
                </div>
              </div>
            </div>

            {found.already_connected && (
              <p role="alert" className="mt-2 text-xs text-amber-700">
                Этот канал уже подключён.
              </p>
            )}

            {found.type === 'group' ? (
              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-muted-fg" htmlFor="channel-token">
                  Токен сообщества
                </label>
                <Input id="channel-token" className="w-full" value={token} onChange={(e) => setToken(e.target.value)} />
                <p className="mt-1 text-[11px] text-muted-fg">
                  Настройки сообщества → Работа с API → Ключи доступа. Токен хранится в зашифрованном виде.
                </p>
              </div>
            ) : (
              <p className="mt-3 text-[11px] text-muted-fg">
                Личный аккаунт подключается через расширение Chrome: пароль и cookie на сервер не передаются.
              </p>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-xs text-red-600">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={close}>
            Отмена
          </Button>
          <Button
            type="button"
            disabled={!found || found.already_connected || pending || (found.type === 'group' && token.trim() === '')}
            onClick={() => void connect()}
          >
            {pending ? 'Подключаю…' : 'Подключить'}
          </Button>
        </div>
      </div>
    </div>
  );
}
